import { useCallback, useMemo } from 'react';

import { WRAPPER_TO_PROVIDER_EVENT_TYPES } from '@/features/event-handlers/wrapper-event-handlers/provider-events/types';
import { Provider } from '@/features/game-context/types';
import { handleUnknownEvent } from '@/features/game-window-integration/helpers/generic-helpers';
import {
  AwagerEventData,
  AwagerEvents,
} from '@/features/game-window-integration/providers/awager/types';
import { useAwagerHandlers } from '@/features/game-window-integration/providers/awager/use-awager-handlers';
import { ProviderLoggerMessages } from '@/features/game-window-integration/types';
import {
  DdEventCategory,
  DdEventType,
  Logger,
} from '@/features/logging/logger';

const useAwagerEventHandlers = () => {
  const {
    handleGameReady,
    handleNotImplemented,
    handleSpinInProgress,
    handleShowDeposit,
    handleCloseGame,
    getGameProviderFrame,
    handleError,
    handleSoundOff,
    handleSoundOn,
    handleGameSwitch,
  } = useAwagerHandlers();

  const providerEventHandlerMapper = useMemo(
    () => ({
      [AwagerEvents.GAME_READY]: handleGameReady,
      [AwagerEvents.ROUND_START]: handleSpinInProgress,
      [AwagerEvents.ROUND_END]: handleSpinInProgress,
      [AwagerEvents.BACK_TO_PARTNER]: handleCloseGame,
      [AwagerEvents.GAME_FINISHED]: handleNotImplemented,
      [AwagerEvents.SHOW_QUICK_DEPOSIT]: handleShowDeposit,
      [AwagerEvents.SOUND_ON]: handleSoundOn,
      [AwagerEvents.SOUND_OFF]: handleSoundOff,
      [AwagerEvents.MACHINE_LOAD_ENDED]: handleNotImplemented,
      [AwagerEvents.LIMIT_REACHED]: handleError,
      [AwagerEvents.CRITICAL_ERROR]: handleError,
      [AwagerEvents.SESSION_EXPIRED]: handleError,
      [AwagerEvents.OUT_OF_FUNDS]: handleNotImplemented,
      [AwagerEvents.GAME_SWITCH]: handleGameSwitch,
    }),
    [
      handleGameReady,
      handleNotImplemented,
      handleSpinInProgress,
      handleShowDeposit,
      handleCloseGame,
      handleError,
      handleSoundOff,
      handleSoundOn,
      handleGameSwitch,
    ],
  );

  const providerEventHandler = useCallback(
    ({ data }: { data: AwagerEventData }) => {
      if (data?.type) {
        if (providerEventHandlerMapper[data?.type]) {
          providerEventHandlerMapper[data?.type]?.(data);
        } else {
          handleUnknownEvent(data);
        }
      }
    },
    [providerEventHandlerMapper],
  );

  const wrapperEventHandler = useCallback(
    (e: MessageEvent) => {
      const executeEvent = (event: AwagerEventData) => {
        const gameProviderFrame = getGameProviderFrame();

        if (gameProviderFrame) {
          gameProviderFrame.postMessage(event, '*');
        } else {
          Logger.rumError({
            name: ProviderLoggerMessages.FAILED_HANDLER_FETCH.replace(
              '%{provider}',
              Provider.AWAGER,
            ),
            useCase: DdEventCategory.ProviderIntegration,
            eventType: DdEventType.Error,
            context: {
              provider: Provider.AWAGER,
            },
          });
        }
      };

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.RELOAD_BALANCE) {
        executeEvent({
          source: 'FD',
          type: AwagerEvents.UPDATE_BALANCE,
        });
      }

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.TOGGLE_HELP) {
        executeEvent({
          source: 'FD',
          type: AwagerEvents.HOW_TO_PLAY,
        });
      }

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.TOGGLE_PAYTABLE) {
        executeEvent({
          source: 'FD',
          type: AwagerEvents.PAYTABLE,
        });
      }

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.TOGGLE_AUDIO) {
        executeEvent({
          source: 'FD',
          type: e.data.payload ? AwagerEvents.SOUND_ON : AwagerEvents.SOUND_OFF,
        });
      }
    },
    [getGameProviderFrame],
  );

  return { wrapperEventHandler, providerEventHandler };
};

export default useAwagerEventHandlers;
